import Enumerable from "linq";
import * as Moment from "moment";
import { IInternalScriptProcessible } from "..";
import { IClaioRepository, IClaioImageSource } from "../../../../domain/claio";
import { IOcojoRepository } from "../../../../domain/ocojo";
import { IExternalImageRepository } from "../../../../domain/externalImage";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import store from "../../../../store";
import { IClaioImageStockable, instanceOfIClaioImageStockable } from "../../../claio/IClaioImageStockable";
import { IOcojoImageStockable, instanceOfIOcojoImageStockable } from "../../../ocojo/IOcojoImageStockable";
import { IExternalImageStockable, instanceOfIExternalImageStockable } from "../../../externalImage/IExternalImageStockable";
import { Arguments } from ".";

export class ImageDateTimeGetter implements IInternalScriptProcessible {
  constructor(claioImageRepository: IClaioRepository, ocojoImageRepository: IOcojoRepository, externalImageRepository: IExternalImageRepository) {
    this._claioStock = instanceOfIClaioImageStockable(claioImageRepository)
      ? claioImageRepository
      : { getStockedClaioImage: (p: string, i: string) => null };
    this._ocojoStock = instanceOfIOcojoImageStockable(ocojoImageRepository)
      ? ocojoImageRepository
      : { getStockedOcojoImage: (p: string, i: string) => null };
    this._externalImageStock = instanceOfIExternalImageStockable(externalImageRepository)
      ? externalImageRepository
      : { getStockedExternalImage: (p: string, i: string) => null };
  }

  private readonly _claioStock: IClaioImageStockable;
  private readonly _ocojoStock: IOcojoImageStockable;
  private readonly _externalImageStock: IExternalImageStockable;
  
  public isProcessible(request: ExecuteRequest): boolean {
    const configuration = store.state.configuration.behavior.localhost.internalScript.scripts.imageDateTimeGetter;
    return configuration.isEnable
        && (new RegExp(configuration.pathExpression, "i")).test(request.exePath)
        && this.hasValue(request);
  }

  private hasValue(request: ExecuteRequest): boolean {
    const imageLinkItem = this.extractImageLinkItem(request);
    if (!imageLinkItem) { throw new Error("画像の指定情報がありません。"); }
    if (imageLinkItem.claioImageId) {
      return !!this.getStockedClaioOrOcojoImage(request.patientId, imageLinkItem.claioImageId);
}
    if (imageLinkItem.externalImageId) {
      return !!this._externalImageStock.getStockedExternalImage(request.patientId, imageLinkItem.externalImageId);
    }
    return true;
  }

  /**
   * Claio画像とClaioHKD(Ocojo)画像のいずれかを取得する
   * ※imageIdの区別がつかないので、ClaioタブとClaioHKDタブの両方を開く運用はできない
   * 　どちらかにだけ情報が積まれているはず
   */
  private getStockedClaioOrOcojoImage(patientId: string, imageId: string): IClaioImageSource {
    const claioImage = this._claioStock.getStockedClaioImage(patientId, imageId);
    return claioImage
      ? claioImage
      : this._ocojoStock.getStockedOcojoImage(patientId, imageId);
  }

  private extractImageLinkItem(request: ExecuteRequest): ScriptLinkItem {
    const arg = this.toArguments(request);
    return Enumerable.from(request.scriptLinkItems)
      .where(i => i.name === arg.getValue("-image"))
      .firstOrDefault();
  }

  private toArguments(request: ExecuteRequest): Arguments {
    return new Arguments(request.arguments);
  }

  public async process(request: ExecuteRequest): Promise<ExternalScriptExecuteResult> {
    const arg = this.toArguments(request);
    const imageLinkItem = Enumerable.from(request.scriptLinkItems)
      .where(i => i.name === arg.getValue("-image"))
      .firstOrDefault();
    
    const dateLinkItem = Enumerable.from(request.scriptLinkItems)
      .where(i => i.name === arg.getValue("-date"))
      .firstOrDefault();
    
    if (!imageLinkItem || !dateLinkItem) { return new ExternalScriptExecuteResult(); }
    const dateText = this.getImageDateText(request.patientId, imageLinkItem);
    return this.toResult(request, dateLinkItem.name, dateText);
  }

  private getImageDateText(patientId: string, item: ScriptLinkItem): string {
    if (!item.claioImageId && !item.externalImageId) {
      // 画像が無い場合
      // TODO: 元の値を残したほうがよい？
      return "00000000";
    }
    const date = item.claioImageId
      ? this.getStockedClaioImageDateTime(patientId, item.claioImageId)
      : this.getStockedExternalImageDateTime(patientId, item.externalImageId);
    
    return date
      ? Moment(date).format("YYYYMMDD")
      : "00000000";
  }

  private getStockedClaioImageDateTime(patientId: string, imageId: string): Date {
    const image = this.getStockedClaioOrOcojoImage(patientId, imageId);
    return image
      ? image.imageDate
      : null;
  }

  private getStockedExternalImageDateTime(patientId: string, imageId: string): Date {
    const image = this._externalImageStock.getStockedExternalImage(patientId, imageId);
    return image
      ? image.imageDateTime
      : null;
  }

  private toResult(request: ExecuteRequest, name: string, value: string): ExternalScriptExecuteResult {
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.name = undefined;
    result.values = [];
    result.compressedCsvText = "";
    result.scriptLinkResults = Enumerable.from(request.scriptLinkItems)
      .where(i => i.name === name)
      .select(i => this.toClonedItem(i, value))
      .toArray();
    return result;
  }

  private toClonedItem(source: ScriptLinkItem, value: string): ScriptLinkItem {
    // TODO: clone使ったほうが良さそう
    const result = new ScriptLinkItem();
    result.name = source.name;
    result.subName = source.subName;
    result.objectId = source.objectId;
    result.itemName = source.itemName;
    result.value = value;
    return result;
  }
}
